import { useCallback } from 'react'
import fileDownload from 'js-file-download'
import { useSelector, useDispatch } from 'react-redux'

import { useFeedback } from './feedback-hook'
import { useDownloadFile } from './download-functions'
import { useCheckBackendR } from './utils-functions'
import {
  useAlgoritmiRunMutation,
  useAlgoritmiExcelMutation,
  useGetBackendRQuery,
} from './api-slice'
import { setOptimisticBackendR } from '../container/home'

export const useRunAlgorithm = () => {
  const { idArbitro } = useSelector(state => state.home.infoUtente)
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const availableCategorie = useSelector(
    state => state.selection.availableCategorie,
  )
  const selectedCategoria = useSelector(
    state => state.selection.selectedCategoria,
  )
  const availableGironi = useSelector(state => state.selection.availableGironi)
  const selectedGirone = useSelector(state => state.selection.selectedGirone)
  const selectedProvvedimento = useSelector(
    state => state.selection.selectedProvvedimento,
  )
  const selectedTeam = useSelector(state => state.selection.selectedTeam)
  const selectedTeams = useSelector(state => state.selection.selectedTeams)
  const selectedNumeriGara = useSelector(
    state => state.selection.selectedNumeriGara,
  )
  const { data: backendR } = useGetBackendRQuery()

  const [algoritmiRunMutation] = useAlgoritmiRunMutation()
  const [algoritmiExcelMutation] = useAlgoritmiExcelMutation()

  const { setFeedback } = useFeedback()
  const { downloadFile } = useDownloadFile()
  const { checkBackendR } = useCheckBackendR()

  const dispatch = useDispatch()

  const setBackendR = useCallback(
    algorithm => {
      const newBackendR = { ...backendR, status: 'busy', process: algorithm }
      dispatch(setOptimisticBackendR(newBackendR))
    },
    [dispatch, backendR],
  )

  const downloadExcel = useCallback(
    async (urlSuffix, body, urlPrefix = 'excel/') => {
      const { blob: fileData, fileName } = await algoritmiExcelMutation({
        urlSuffix,
        body,
        urlPrefix,
      }).unwrap()
      fileDownload(fileData, fileName)
    },
    [algoritmiExcelMutation],
  )

  const runAlgorithm = useCallback(
    async params => {
      const {
        algorithm,
        triggerDownload,
        exactlyOne,
        type,
        selectedRimborso,
        selectedRendiconto,
        selectedDistinta,
      } = params

      if (!algorithm) {
        setFeedback('Algoritmo non definito!')
        return
      }

      if (algorithm === '01_GenerateTable') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
      }

      if (algorithm === '07_GenerateExcelGironi') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
        if (!selectedCategoria) {
          setFeedback('Categoria non definita!')
          return
        }
      }

      if (algorithm === '08_GenerateExcelCalendari') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
        if (!selectedCategoria) {
          setFeedback('Categoria non definita!')
          return
        }
        if (!selectedGirone) {
          setFeedback('Girone non definito!')
          return
        }
      }

      if (algorithm === '09_GenerateExcelProvvedimenti') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
        if (!selectedProvvedimento) {
          setFeedback('Provvedimento non definito!')
          return
        }
      }

      if (algorithm === '11_ComputeStats') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
      }

      if (algorithm === '12_GenerateExcelDesignazioni') {
        if (!selectedCampionato) {
          setFeedback('Campionato non definito!')
          return
        }
      }

      if (algorithm === '13_GenerateExcelRimborso') {
        if (!selectedRimborso) {
          setFeedback('Rimborso non definito!')
          return
        }
      }

      if (algorithm === '15_GenerateExcelRendiconto') {
        if (!selectedRendiconto && !selectedStagione) {
          setFeedback('Rendiconto e stagione non definiti!')
          return
        }
      }

      if (algorithm === '16_GenerateExcelAutocertificazione') {
        if (!idArbitro) {
          setFeedback('Arbitro non definito!')
          return
        }
      }

      if (algorithm === '17_GenerateExcelIscrizioniCorsi') {
        if (!selectedStagione) {
          setFeedback('Stagione non definita!')
          return
        }
      }

      if (algorithm === '20_GenerateDistinta') {
        if (!selectedDistinta) {
          setFeedback('Distinta non definita!')
          return
        }
      }

      try {
        await checkBackendR()
      } catch (err) {
        setFeedback(err.message)
        return
      }

      try {
        if (algorithm === '01_GenerateTable') {
          setBackendR(algorithm)
          const urlCategoria =
            selectedCategoria && selectedCategoria !== availableCategorie[0]
              ? `/${selectedCategoria}`
              : ''
          await algoritmiRunMutation({
            algorithm,
            urlSuffix: `/${selectedCampionato}${urlCategoria}`,
          }).unwrap()
        }

        if (algorithm === '02_GenerateGironi') {
          setBackendR(algorithm)
          const urlCampionato = selectedCampionato
            ? `/${selectedCampionato}`
            : ''
          await algoritmiRunMutation({
            algorithm,
            urlSuffix: urlCampionato,
          }).unwrap()
        }

        if (algorithm === '03_GenerateCalendari') {
          setBackendR(algorithm)
          const urlCampionato = selectedCampionato
            ? `/${selectedCampionato}`
            : ''
          await algoritmiRunMutation({
            algorithm,
            urlSuffix: urlCampionato,
          }).unwrap()
        }

        if (algorithm === '07_GenerateExcelGironi') {
          const urlSuffix = `gironi/${selectedCampionato}/${selectedCategoria}`
          const body = selectedTeams ? { selectedTeams } : undefined
          await downloadExcel(urlSuffix, body)
        }

        if (algorithm === '08_GenerateExcelCalendari') {
          const urlGirone =
            selectedGirone === availableGironi[0] ? 'all' : selectedGirone
          const urlTeam = selectedTeam ? `/${selectedTeam}` : ''
          await downloadExcel(
            `calendari/${selectedCampionato}/${selectedCategoria}/${urlGirone}${urlTeam}`,
          )
        }

        if (algorithm === '09_GenerateExcelProvvedimenti') {
          await downloadExcel(
            `provvedimenti/${selectedCampionato}/${selectedProvvedimento}`,
            { numeriGara: selectedNumeriGara },
          )
        }

        if (algorithm === '10_GenerateExcelArbitri') {
          await downloadExcel('arbitri')
        }

        if (algorithm === '11_ComputeStats') {
          await algoritmiRunMutation({
            algorithm,
            urlSuffix: `/${selectedCampionato}`,
          }).unwrap()
        }

        if (algorithm === '12_GenerateExcelDesignazioni') {
          const body = { numeriGara: selectedNumeriGara }
          await downloadExcel(`designazioni/${selectedCampionato}`, body)
        }

        if (algorithm === '13_GenerateExcelRimborso') {
          await downloadExcel(`rimborsi/${selectedRimborso}`)
        }

        if (algorithm === '14_GenerateExcelAffiliazioni') {
          await downloadExcel('affiliazioni')
        }

        if (algorithm === '15_GenerateExcelRendiconto') {
          const urlSuffix = selectedRendiconto
            ? `rendiconti/${selectedRendiconto}`
            : `rendiconti/stagione/${selectedStagione}`
          await downloadExcel(urlSuffix)
        }

        if (algorithm === '16_GenerateExcelAutocertificazione') {
          await downloadExcel(
            `rimborsi/autocertificazione/arbitro/${idArbitro}`,
          )
        }

        if (algorithm === '17_GenerateExcelIscrizioniCorsi') {
          await downloadExcel(`iscrizioni-corsi/stagione/${selectedStagione}`)
        }

        if (algorithm === '18_GenerateExcelRimborsiBanca') {
          await downloadExcel('rimborsi-banca')
        }

        if (algorithm === '20_GenerateDistinta') {
          await downloadExcel(`distinta/${selectedDistinta}`, undefined, '')
        }

        if (triggerDownload) downloadFile(algorithm, type, exactlyOne)
      } catch (err) {
        setFeedback(err?.data?.message || err?.message)
      }
    },
    [
      algoritmiRunMutation,
      availableCategorie,
      availableGironi,
      checkBackendR,
      downloadExcel,
      downloadFile,
      idArbitro,
      selectedCampionato,
      selectedCategoria,
      selectedGirone,
      selectedProvvedimento,
      selectedNumeriGara,
      selectedStagione,
      selectedTeam,
      selectedTeams,
      setBackendR,
      setFeedback,
    ],
  )

  return { runAlgorithm }
}
